import {
  Badge,
  DatatableCell,
  DatatableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { FolderIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Brief, BriefStatus } from '@/entities/brief';
import { i18n } from '@/shared/i18n';
import { BRIEF_TYPE_LABEL } from '../../briefs.constants';
import { BriefsActionMenu } from '../BriefsActionMenu';
import { extractTipTapText } from './extractTipTapText';

interface BriefsTableRowProps {
  brief: Brief;
  /** Position in the list — drives the staggered entrance animation. */
  /** Fires when the user clicks the row (or activates it via keyboard). */
  onClick: (brief: Brief) => void;
}

/**
 * Single-row presentation of a {@link Brief} for the Figma-redesigned briefs
 * list view (MFINS-2654).
 *
 * Columns:
 *   1. **Title** — composite cell with a 40px `bg-surface` icon badge
 *      (FileText) + stacked title and (when available) a first-section
 *      preview subtitle (plain text, no icon). Preview text is the
 *      plain-text projection of the first section's TipTap document
 *      (sorted by `position`), capped at two lines via `line-clamp-2`
 *      so overflow is ellipsed by the browser. When the brief has no
 *      sections or the first section is empty, the entire subtitle row
 *      is omitted (no placeholder copy).
 *   2. **Type** — outline badge with a per-type icon ({@link BRIEF_TYPE_ICON})
 *      and the {@link BRIEF_TYPE_LABEL} label. Em-dash `'—'` when
 *      `briefType` is `undefined`.
 *   3. **Status** — fully circular `Badge` (`roundness="full"`) with no
 *      icon: `secondary` (gray) for `draft`, `positive` (green) for
 *      `submitted`. Labels: `Draft` / `Ready` — `submitted` renders as
 *      "Ready" per MFINS-3296 (UI-only relabel; BE enum unchanged).
 *   4. **Last modified** — relative time via {@link formatRelativeDate},
 *      muted text only (no icon).
 *   5. **Actions** — trailing kebab menu ({@link BriefsActionMenu}) with
 *      Edit / Duplicate / Download PDF / Copy as Markdown / Delete. Its
 *      trigger + delete-modal stop click propagation so opening the menu
 *      never fires the row's navigation.
 *
 * No "Created" column — dropped per the redesign.
 *
 * Migrated to monks-ui `Datatable*` (MFINS-2897 Option-A pivot):
 *   - Outer row is `<DatatableRow interactive>` — the `interactive`
 *     variant gives the row `cursor-pointer` + `hover:[&>*]:bg-muted` +
 *     `active:[&>*]:bg-surface-raised` (child-cell selectors so hover reads
 *     in the `border-separate` layout the pivot switches to).
 *   - Cells are `DatatableCell` (from monks-ui) — same DOM shape as the old
 *     `TableCell`, richer variant surface.
 *   - Leading icon framing is `<TableIconBadge>` from `@/shared/ui/data-table`.
 */
export function BriefsTableRow(props: BriefsTableRowProps) {
  const { brief, onClick } = props;
  const { t } = useTranslation('projects');
  const linkedFolders = brief.approvalFolders;

  const handleClick = () => {
    onClick(brief);
  };

  const typeLabel = brief.briefType ? BRIEF_TYPE_LABEL[brief.briefType] : EM_DASH;

  // Subtitle: prefer the AI-generated summary when available, fall back
  // to the first section's plain-text projection for older briefs.
  const sortedSections = [...brief.sections].sort((a, b) => a.position - b.position);
  const subtitle = brief.summary ?? extractTipTapText(sortedSections[0]?.content ?? undefined);

  return (
    <DatatableRow
      interactive
      data-testid="brief-row"
      className="h-20 text-sm font-normal text-foreground"
      onClick={handleClick}
    >
      {/* Title column — the design stacks title over a one-line summary and
          drops the leading icon badge; both branches leave the column
          width-free so it fills the row. */}
      <DatatableCell>
        <div className="flex min-w-0 flex-col gap-3xs">
          <div className="flex min-w-0 items-center gap-2xs">
            <span className="truncate text-sm font-medium text-foreground" title={brief.title}>
              {brief.title}
            </span>
            {/* An indicator, not a control: the row already navigates to the
                brief, and a second target inside it would compete for the
                click. Which folder is the part worth saying, so it goes in
                the tooltip rather than being left to the icon alone. */}
            {linkedFolders.length > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      data-testid="brief-linked-folder-indicator"
                      aria-label={t('projectDetail.briefsTableRow.deliveredInto', {
                        count: linkedFolders.length,
                        names: linkedFolders.map((folder) => folder.name).join(', '),
                      })}
                      className="shrink-0 text-muted-foreground"
                    >
                      <FolderIcon className="size-3.5" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('projectDetail.briefsTableRow.deliveredInto', {
                      count: linkedFolders.length,
                      names: linkedFolders.map((folder) => folder.name).join(', '),
                    })}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          {subtitle ? (
            <p data-testid="brief-subtitle" className="truncate text-xs text-foreground-subtle">
              {subtitle}
            </p>
          ) : null}
        </div>
      </DatatableCell>

      {/* Type column — outline pill with per-type icon + label, per the Figma
          data-table (intentionally diverges from the Projects filled Type chip).
          `bg-transparent` overrides the outline variant's default white fill so
          only the border/icon/text show against the row. */}
      <DatatableCell>
        <span>{typeLabel}</span>
      </DatatableCell>

      {/* Status column — secondary (draft) | positive (submitted). Pill
          shape (`roundness="full"`) per the polish pass. */}
      <DatatableCell>
        <Badge
          data-testid="brief-status-badge"
          data-variant={STATUS_BADGE_VARIANT[brief.status]}
          variant={STATUS_BADGE_VARIANT[brief.status]}
          roundness="full"
          size="sm"
        >
          {STATUS_LABEL[brief.status]}
        </Badge>
      </DatatableCell>

      {/* Actions — row kebab. Width-pinned to match the header column. */}
      <DatatableCell>
        <BriefsActionMenu brief={brief} />
      </DatatableCell>
    </DatatableRow>
  );
}

const STATUS_BADGE_VARIANT: Record<BriefStatus, 'secondary' | 'positive'> = {
  draft: 'secondary',
  submitted: 'positive',
};

// Getters so each read resolves against the active locale; a plain `i18n.t(...)` here would
// freeze the English at import time.
const STATUS_LABEL: Record<BriefStatus, string> = {
  get draft() {
    return i18n.t('projects:projectDetail.briefsConstants.draft');
  },
  get submitted() {
    return i18n.t('projects:projectDetail.briefsConstants.ready');
  },
};

/** Em-dash placeholder for nullable `briefType`. */
const EM_DASH = '—';
